import React from 'react';
import { PlusOutlined } from '@ant-design/icons';
import PageToolbar from 'ui/PageToolbar/PageToolbar';
import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Space, Button, Modal } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
// Api
import { DELETE_PSYCHOSOCIAL_KEYWORD } from 'api/psychosocialKeyword/mutations';
import { GET_ALL_PSYCHOSOCIAL_KEYWORDS } from 'api/psychosocialKeyword/queries';
// Types
import {
  DeletePsychosocialKeyword,
  DeletePsychosocialKeywordVariables,
} from 'api/psychosocialKeyword/types/DeletePsychosocialKeyword';
import { PsychosocialKeywordFilterInput } from 'api/graphql-global-types';
import {
  GetAllUserPsychosocialKeywords,
  GetAllUserPsychosocialKeywordsVariables,
  GetAllUserPsychosocialKeywords_getAllUserPsychosocialKeywords_entities,
} from 'api/psychosocialKeyword/types/GetAllUserPsychosocialKeywords';
// UI
import { errorNotification, successNotification } from 'ui/Notification';
import { TableFilter } from 'ui/Table';
import Table from 'ui/Table';
// Components
import CreateEditPsychosocialKeywords from './components/CreateEditPsychosocialKeywords/CreateEditPsychosocialKeywords';
import Stores from './components/Stores/Stores';
// Styles

const { confirm } = Modal;

const ManagePsychosocialKeywords = (): JSX.Element => {
  const [pageSize, setPageSize] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);

  const [searchFilters, setSearchFilters] = useState<TableFilter[]>([]);

  const getInput = (): GetAllUserPsychosocialKeywordsVariables => {
    const input: PsychosocialKeywordFilterInput = {
      limit: pageSize,
      offset: (currentPage > 1 ? currentPage - 1 : 0) * pageSize,
    };

    searchFilters?.forEach(({ key, value }) => {
      input[key as keyof PsychosocialKeywordFilterInput] = value as any;
    });

    return { input };
  };

  const [selectedRecord, setSelectedRecord] =
    useState<GetAllUserPsychosocialKeywords_getAllUserPsychosocialKeywords_entities | null>(
      null
    );

  const [isCreateModalVisible, setIsCreateModalVisible] =
    useState<boolean>(false);

  const [isEditModalVisible, setIsEditVisible] = useState<boolean>(false);

  const { data, loading } = useQuery<
    GetAllUserPsychosocialKeywords,
    GetAllUserPsychosocialKeywordsVariables
  >(GET_ALL_PSYCHOSOCIAL_KEYWORDS, {
    variables: { ...getInput() },
    fetchPolicy: 'cache-and-network',
  });

  const [deletePsychosocialKeyword, { loading: deleteAreaLoading }] =
    useMutation<DeletePsychosocialKeyword, DeletePsychosocialKeywordVariables>(
      DELETE_PSYCHOSOCIAL_KEYWORD
    );

  const hideCreateEditModal = () => {
    setIsEditVisible(false);
    setIsCreateModalVisible(false);
    setSelectedRecord(null);
  };

  const showEditModal = (
    record: GetAllUserPsychosocialKeywords_getAllUserPsychosocialKeywords_entities | null
  ) => {
    setSelectedRecord(record);
    setIsEditVisible(true);
  };

  const handleShowAddProductModal = () => {
    setIsCreateModalVisible(true);
  };

  const [isStoresModalVisible, setIsStoresModalVisible] =
    useState<boolean>(false);

  const showStoresModal = (
    record: GetAllUserPsychosocialKeywords_getAllUserPsychosocialKeywords_entities | null
  ) => {
    setSelectedRecord(record);
    setIsStoresModalVisible(true);
  };

  const hideStoresModal = () => {
    setIsStoresModalVisible(false);
    setSelectedRecord(null);
  };

  const handleDelete = async (id: string) => {
    confirm({
      title: 'Are you sure you want to delete this item?',
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        try {
          await deletePsychosocialKeyword({
            variables: {
              id,
            },
            refetchQueries: [
              {
                query: GET_ALL_PSYCHOSOCIAL_KEYWORDS,
                variables: { ...getInput() },
              },
            ],
          });
          successNotification('Successfully removed');
        } catch (err) {
          errorNotification(
            'Looks like something went wrong. Please try again later.'
          );
        }
      },
    });
  };

  const columns = [
    {
      title: 'Item',
      dataIndex: 'name',
      key: 'keyword',
      fixed: 'left' as const,
      align: 'center' as const,
      withSearch: true,
      width: 150,
    },
    {
      title: 'Stores',
      dataIndex: 'storeCount',
      align: 'center' as const,
      width: 150,
      render: function StoresNumber(
        text: string,
        record: GetAllUserPsychosocialKeywords_getAllUserPsychosocialKeywords_entities
      ) {
        return (
          <Button
            disabled={record?.storeCount === 0}
            type="link"
            onClick={() => showStoresModal(record)}
            title={`Manage stores with internal tag: ${record?.name}`}
          >
            {record?.storeCount || '-'}
          </Button>
        );
      },
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      fixed: 'right' as const,
      align: 'center' as const,
      width: 120,
      render: function Actions(
        text: string,
        record: GetAllUserPsychosocialKeywords_getAllUserPsychosocialKeywords_entities
      ) {
        return (
          <Space size="middle" direction="vertical">
            <Button
              type="primary"
              onClick={() => {
                showEditModal(record);
              }}
            >
              Edit
            </Button>

            <Button type="default" onClick={() => handleDelete(record.id)}>
              Remove
            </Button>
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      <PageToolbar leading={<h1>Manage Internal Tags</h1>}>
        <Button
          icon={<PlusOutlined />}
          type="primary"
          onClick={handleShowAddProductModal}
        >
          Add new item
        </Button>
      </PageToolbar>

      <Modal
        title={selectedRecord?.id ? 'Edit item' : 'New item'}
        visible={
          isCreateModalVisible || (!!selectedRecord && isEditModalVisible)
        }
        onCancel={hideCreateEditModal}
        className="createEditItemModal"
        footer={null}
        destroyOnClose
      >
        <CreateEditPsychosocialKeywords
          currentAction={selectedRecord?.id ? 'Edit' : 'New'}
          record={selectedRecord}
          hideItemModal={hideCreateEditModal}
          getInput={getInput}
        />
      </Modal>

      <Modal
        title={`Store with internal tag ${selectedRecord?.name || ''}`}
        visible={isStoresModalVisible}
        onCancel={hideStoresModal}
        footer={null}
        destroyOnClose
        width="50vw"
      >
        {selectedRecord && (
          <Stores
            record={selectedRecord}
            getInput={getInput}
            onFinish={hideStoresModal}
          />
        )}
      </Modal>

      <Table<GetAllUserPsychosocialKeywords_getAllUserPsychosocialKeywords_entities>
        columns={columns}
        data={data?.getAllUserPsychosocialKeywords?.entities}
        scroll={{ x: 800 }}
        loading={loading || deleteAreaLoading}
        total={data?.getAllUserPsychosocialKeywords?.total}
        setPageSize={setPageSize}
        setCurrentPage={setCurrentPage}
        searchFilters={searchFilters}
        setSearchFilters={setSearchFilters}
      />
    </div>
  );
};

export default ManagePsychosocialKeywords;
